import {
  ISupplierStatement,
  IThirdPartyInvoice,
  SupplierStatement,
  SupplierStatementPatchRequest,
} from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import { coreApi, stockApi } from '$api'
import { Dayjs } from 'dayjs'

enum QueryKeys {
  getSupplierStatements = 'supplierStatements',
}

/**
 * Get Supplier Statements
 * @param from - The start date for filtering supplier statements
 * @param to - The end date for filtering supplier statements
 * @param supplierId - The supplier id to filter by (optional)
 * @param includeDeleted - Whether or not to include deleted statements, false by default
 * @returns a list of Supplier Statements
 */
export const getSupplierStatements = async (
  from: Dayjs,
  to: Dayjs,
  supplierId?: string,
  includeDeleted: boolean = false
): Promise<ISupplierStatement[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getSupplierStatements,
      { from, to, supplierId, includeDeleted },
    ],
    queryFn: async () => {
      return await stockApi.getSupplierStatements(
        from,
        to,
        supplierId,
        includeDeleted
      )
    },
  })
}

/**
 * Get Supplier Statement
 * @param id - Supplier Statement id
 * @returns ISupplierStatement
 */
export const getSupplierStatement = async (
  id: string
): Promise<ISupplierStatement> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.getSupplierStatements, { id }],
    queryFn: async () => {
      return await stockApi.getSupplierStatement(id)
    },
  })
}

/**
 * Update Supplier Statement
 * @param id - Supplier Statement id
 * @param supplierStatement - supplier statement to update
 * @returns ISupplierStatement
 */
export const updateSupplierStatement = async (
  id: string,
  supplierStatement: ISupplierStatement
): Promise<ISupplierStatement> => {
  return await stockApi.updateSupplierStatement(
    id,
    supplierStatement as SupplierStatement
  )
}

/**
 * Create Supplier Statement
 * @param supplierStatement - supplier statement to create
 * @returns ISupplierStatement
 */
export const createSupplierStatement = async (
  supplierStatement: ISupplierStatement
): Promise<ISupplierStatement> => {
  return await stockApi.createSupplierStatement(
    supplierStatement as SupplierStatement
  )
}

/**
 * Export Supplier Statements to Accounting System
 * @param combine - wether to combine them or not
 * @param supplierStatementsIds - array of supplier statements ids
 */
export const exportToAccountingSystem = async (
  combine: boolean,
  supplierStatementsIds: string[]
): Promise<IThirdPartyInvoice[]> => {
  return await coreApi.exportStatements(combine, supplierStatementsIds)
}

/**
 * Update supplier statement third party invoice id
 * @param thirdPartyInvoiceId - third party invoice id
 * @param supplierStatementId - supplier statement id
 * @returns ISupplierStatement
 */
export const updateThirdPartyInvoiceId = async (
  supplierStatementId: string,
  thirdPartyInvoiceId?: string
): Promise<ISupplierStatement | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return await stockApi.updateSupplierStatementThirdPartyInvoiceId(
    supplierStatementId,
    new SupplierStatementPatchRequest({
      thirdPartyInvoiceId,
    })
  )
}

/**
 * Delete supplier statement
 * @param supplierStatementId - supplier statement id
 * @returns
 */
export const deleteSupplierStatement = async (supplierStatementId: string) => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return await stockApi.deleteSupplierStatement(supplierStatementId)
}
